import { useSupabase } from '@/hooks/useSupabase'
import { supabase } from '@/lib/supabase'
import type { BlindSpot } from '@/types'

interface ConceptWithDomainJoin {
  domain_id: string | null
  domain: { name: string } | null
}

export function useBlindSpots() {
  return useSupabase<BlindSpot[]>(async () => {
    const spots: BlindSpot[] = []

    // 1. Domain gaps — find domains with fewer than 3 concepts
    const { data: domainCounts } = await supabase
      .from('concepts')
      .select('domain_id, domain:domains(name)')

    if (domainCounts) {
      const counts = new Map<string, { name: string; count: number }>()
      for (const c of domainCounts) {
        const row = c as unknown as ConceptWithDomainJoin
        const domainName = row.domain?.name || 'Sem domínio'
        const did = c.domain_id || 'none'
        const existing = counts.get(did)
        if (existing) {
          existing.count++
        } else {
          counts.set(did, { name: domainName, count: 1 })
        }
      }

      // Get all domains
      const { data: allDomains } = await supabase.from('domains').select('id, name')
      if (allDomains) {
        for (const domain of allDomains) {
          const domainInfo = counts.get(domain.id)
          if (!domainInfo || domainInfo.count < 3) {
            spots.push({
              type: 'domain_gap',
              title: `Domínio pouco explorado: ${domain.name}`,
              description: `O domínio "${domain.name}" tem apenas ${domainInfo?.count || 0} conceito(s). Considere ingerir mais conteúdo sobre este tema.`,
              impact: domainInfo?.count === 0 ? 'high' : 'medium',
              suggestion: `Busque artigos ou vídeos sobre ${domain.name} para expandir seu conhecimento.`,
            })
          }
        }
      }
    }

    // 2. Depth gaps — concepts stuck at low complexity levels
    const { data: shallowConcepts } = await supabase
      .from('concepts')
      .select('id, name, complexity_level, synapse_strength')
      .lt('complexity_level', 3)
      .gt('synapse_strength', 0.3)
      .order('synapse_strength', { ascending: false })
      .limit(5)

    if (shallowConcepts) {
      for (const concept of shallowConcepts) {
        spots.push({
          type: 'depth_gap',
          title: `Conceito raso: ${concept.name}`,
          description: `"${concept.name}" tem força ${Math.round(concept.synapse_strength * 100)}% mas está no nível ${concept.complexity_level}. Aprofunde-se.`,
          impact: 'medium',
          suggestion: `Explore conexões de "${concept.name}" com outros conceitos para elevar a complexidade.`,
          related_nodes: [concept.id],
        })
      }
    }

    // 3. Connection gaps — concepts with 0 connections
    const { data: isolatedConcepts } = await supabase
      .from('concepts')
      .select('id, name')
      .eq('mesh_eligible', true)
      .limit(100)

    if (isolatedConcepts && isolatedConcepts.length > 0) {
      const conceptIds = isolatedConcepts.map(c => c.id)
      // Fetch connections for these concepts (split into 2 safe queries)
      const { data: connFrom } = await supabase
        .from('connections')
        .select('from_node_id, to_node_id')
        .in('from_node_id', conceptIds)
      const { data: connTo } = await supabase
        .from('connections')
        .select('from_node_id, to_node_id')
        .in('to_node_id', conceptIds)
      const connections = [...(connFrom || []), ...(connTo || [])]

      const connectedIds = new Set<string>()
      for (const conn of connections) {
        connectedIds.add(conn.from_node_id)
        connectedIds.add(conn.to_node_id)
      }

      const isolated = isolatedConcepts.filter(c => !connectedIds.has(c.id))
      if (isolated.length > 0) {
        spots.push({
          type: 'connection_gap',
          title: `${isolated.length} conceito(s) isolado(s)`,
          description: `Conceitos como "${isolated[0].name}" não possuem conexões. Conectá-los fortalece a rede.`,
          impact: isolated.length > 5 ? 'high' : 'medium',
          suggestion: 'Navegue até a Constellation e use o Pathfinder para descobrir conexões.',
          related_nodes: isolated.slice(0, 5).map(c => c.id),
        })
      }
    }

    return spots
  })
}
